/**
 * The six acts' requests (ADR-0044, plan Task 8b).
 *
 * One hook so the case file does not grow six fetch blocks, and so every act
 * fails the same way: a refusal comes back as a thrown `Error` carrying the
 * route's own sentence, which is what the control beside it prints. The routes
 * refuse in words a person can act on ("This Team is paused", "The pull
 * request this ran on is closed"), and inventing a generic message on top of
 * that would throw away the only useful half.
 */
import { useCallback } from "react";
import type { StartOverResponse } from "@yagni/shared";

async function post(url: string, body?: unknown, idempotencyKey?: string): Promise<unknown> {
  const res = await fetch(url, {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...(idempotencyKey ? { "Idempotency-Key": idempotencyKey } : {}),
    },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });
  const payload = (await res.json().catch(() => null)) as { error?: string } | null;
  if (!res.ok) {
    throw new Error(payload?.error ?? "That did not go through.");
  }
  return payload;
}

export interface PageActs {
  /** Reply to one read, addressed by the run that made it. */
  replyToRead: (runId: string, body: string) => Promise<void>;
  /**
   * Leave the note the next build step reads; resolves with whether a step
   * is coming to read it (`stepComing`, absent when the route could not say).
   */
  leaveNextStepNote: (body: string) => Promise<{ stepComing?: boolean }>;
  /**
   * Split the plan into its own tickets. A `repoId` on every child (one of
   * the Team's repositories) makes them an ordered chain, built one at a
   * time (YAG-1058); without, every child plans at once, as before.
   * `idempotencyKey` is the panel's one key: a retry of the same split (a
   * second click, a response lost on the way back) gets the first one's
   * tickets instead of a refusal.
   */
  split: (
    children: { title: string; steps: string[]; repoId?: string }[],
    idempotencyKey?: string,
  ) => Promise<void>;
  /** Back to the first stage with a note. */
  returnToProposed: (note: string) => Promise<void>;
  /**
   * A ticket of one of the plan's follow-ups ("Found along the way"), on the
   * same Team; resolves with the ticket, the one already made on a repeat.
   */
  createFollowUp: (title: string) => Promise<{ id: string; identifier: string }>;
  /** Start the same line again off a stopped run. */
  runAgain: (runId: string) => Promise<void>;
  /**
   * Put the approved plan back at its gate, unchanged, after a stopped build
   * no line stands behind. Starts nothing: approving it again does. This one
   * DOES consume the open stop gate on the way, unlike the acts above; the
   * note, when there is one, lands in that gate's resolution record.
   */
  reofferPlan: (input?: { note?: string; gateId?: string | null }) => Promise<void>;
  /** Throw the work away and start the ticket over; resolves with the new attempt. */
  startOver: (input: {
    reason: string;
    closePullRequest: boolean;
    deleteBranch: boolean;
    /** Start again in another of the Team's repositories, the one the plan says the change belongs in. */
    moveTo?: { repoId: string; why: string };
  }) => Promise<StartOverResponse>;
}

export function usePageActs(workItemId: string, onChanged?: () => void): PageActs {
  const id = encodeURIComponent(workItemId);
  const done = useCallback(() => onChanged?.(), [onChanged]);

  return {
    replyToRead: useCallback(
      async (runId, body) => {
        await post(`/api/work-items/${id}/reads/${encodeURIComponent(runId)}/reply`, { body });
        done();
      },
      [id, done],
    ),
    leaveNextStepNote: useCallback(
      async (body) => {
        const payload = (await post(`/api/work-items/${id}/next-step-note`, { body })) as {
          stepComing?: unknown;
        } | null;
        done();
        return typeof payload?.stepComing === "boolean" ? { stepComing: payload.stepComing } : {};
      },
      [id, done],
    ),
    split: useCallback(
      async (children, idempotencyKey) => {
        await post(`/api/work-items/${id}/split`, { children }, idempotencyKey);
        done();
      },
      [id, done],
    ),
    returnToProposed: useCallback(
      async (note) => {
        await post(`/api/work-items/${id}/return-to-proposed`, { note });
        done();
      },
      [id, done],
    ),
    createFollowUp: useCallback(
      async (title) => {
        const payload = (await post(`/api/work-items/${id}/follow-ups`, { title })) as {
          ticket: { id: string; identifier: string };
        };
        return payload.ticket;
      },
      [id],
    ),
    runAgain: useCallback(
      async (runId) => {
        await post(`/api/runs/${encodeURIComponent(runId)}/again`);
        done();
      },
      [done],
    ),
    reofferPlan: useCallback(
      async (input) => {
        // `gateId` pins the ask the surface was rendered against (null: it
        // rendered none), so an ask that opened or replaced it in the meantime
        // is never the one consumed (404 instead).
        await post(`/api/work-items/${id}/plan/reoffer`, {
          ...(input?.note ? { note: input.note } : {}),
          ...(input?.gateId !== undefined ? { gateId: input.gateId } : {}),
        });
        done();
      },
      [id, done],
    ),
    startOver: useCallback(
      async (input) => {
        const payload = (await post(`/api/work-items/${id}/start-over`, {
          ...(input.reason ? { reason: input.reason } : {}),
          closePullRequest: input.closePullRequest,
          deleteBranch: input.deleteBranch,
          ...(input.moveTo ? { moveTo: input.moveTo } : {}),
        })) as StartOverResponse;
        done();
        return payload;
      },
      [id, done],
    ),
  };
}
